import { CommissionHistory, CommissionStatus } from "types/affiliation.types";
import Table from "common/Table/Table";
import React from "react";
import { ColumnsType } from "antd/es/table";
import {
  FROM_DESKTOP_COLUMN,
  FROM_LAPTOP_COLUMN,
  FROM_TABLET_COLUMN,
} from "common/NewLook/responsiveColumns.ts";
import Text from "common/Typography/Text";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import Status from "features/affiliation/common/Status";
import { getReferralOrPromoCode } from "features/affiliation/common/utils";
import {
  COMMISSION_STATUS_DISPLAY_NAME,
  USD_PRECISION,
} from "features/affiliation/common/constants";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import { formatDate } from "@shared/utils/date";
import { toFixed } from "@shared/utils/numbers";
import { AnalyticsPageName, LanguageNameSpaces } from "@shared/types";
import { checkSearch } from "@shared/utils/misc";
import EarningsEmptyState from "features/affiliation/common/EarningsEmptyState";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useTheme } from "styled-components";
import {
  formatUsd,
  NUMBER_COLUMN,
} from "features/affiliation/NewLook/newLookUtils";
import {
  CellFigure,
  CellText,
  DateCell,
} from "features/affiliation/NewLook/TablePanel";

interface EarningsTableProps {
  commissions?: CommissionHistory[];
  isLoading: boolean;
  searchFilter?: string;
}

const EarningsTable = ({
  commissions,
  isLoading,
  searchFilter,
}: EarningsTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { isNewLook } = useTheme();
  const commissionsWithKey = commissions
    ?.filter((commission) =>
      checkSearch(commission.referral_code, searchFilter)
    )
    .map((commission, index) => {
      return { ...commission, key: index };
    });

  const columns: ColumnsType<CommissionHistory> = [
    {
      title: t("earnings.created_date"),
      dataIndex: "time",
      showSorterTooltip: false,
      defaultSortOrder: "descend",
      sorter: (a, b) => sortByNumberProperty(a, b, "time"),
      render: (time) => <Text>{formatDate(time, true, true)}</Text>,
    },
    {
      title: t("earnings.customer_id"),
      dataIndex: "customer_id",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "customer_id"),
      render: (customerId) => <Text>{customerId}</Text>,
    },
    {
      title: t("earnings.referral_link_promo"),
      dataIndex: "referral_code",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "referral_code"),
      render: (referralCode, commission) => (
        <Text>{getReferralOrPromoCode(referralCode, commission.source)}</Text>
      ),
    },
    {
      title: t("earnings.revenue"),
      dataIndex: "revenue",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "revenue"),
      render: (revenue) =>
        isNewLook ? (
          formatUsd(revenue)
        ) : (
          <Text>${toFixed(revenue, USD_PRECISION)}</Text>
        ),
    },
    {
      title: t("earnings.commission"),
      dataIndex: "commission",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "commission"),
      render: (commission) =>
        isNewLook ? (
          formatUsd(commission)
        ) : (
          <Text>${toFixed(commission, USD_PRECISION)}</Text>
        ),
    },
    {
      title: (
        <div style={{ display: "flex" }}>
          {t("earnings.status")}
          <HelperTooltip
            analyticsFieldName="Status"
            analyticsPageName={AnalyticsPageName.AFFILIATION_EARNINGS}
            description={
              <ul
                style={{
                  paddingInlineStart: 18,
                  margin: 0,
                  paddingBlockStart: 4,
                }}
              >
                <li>{t("earnings.status_tooltip.confirmed")}</li>
                <li>{t("earnings.status_tooltip.pending")}</li>
                <li>{t("earnings.status_tooltip.denied")}</li>
                <li>{t("earnings.status_tooltip.payment_refunded")}</li>
              </ul>
            }
          />
        </div>
      ),
      dataIndex: "status",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "status"),
      render: (status: CommissionStatus) => (
        <Status
          itemStatus={CommissionStatus}
          status={status}
          statusDisplayName={t(COMMISSION_STATUS_DISPLAY_NAME[status])}
        />
      ),
    },
  ];

  // New look: the date over its time, amounts right-aligned.
  const newLookColumns: ColumnsType<CommissionHistory> = [
    { ...columns[0], render: (time) => <DateCell time={time} /> },
    {
      ...columns[1],
      ...FROM_DESKTOP_COLUMN,
      render: (customerId) => <CellText $ink={true}>{customerId}</CellText>,
    },
    {
      ...columns[2],
      ...FROM_TABLET_COLUMN,
      render: (referralCode, commission) => {
        const code = getReferralOrPromoCode(referralCode, commission.source);
        return (
          <CellText title={code} $truncate={true}>
            {code}
          </CellText>
        );
      },
    },
    {
      ...columns[3],
      ...NUMBER_COLUMN,
      ...FROM_LAPTOP_COLUMN,
      render: (revenue) => <CellText>{formatUsd(revenue)}</CellText>,
    },
    {
      ...columns[4],
      ...NUMBER_COLUMN,
      render: (commission) => <CellFigure>{formatUsd(commission)}</CellFigure>,
    },
    columns[5],
  ];

  return (
    <Table<CommissionHistory>
      columns={isNewLook ? newLookColumns : columns}
      dataSource={commissionsWithKey}
      loading={isLoading}
      locale={{
        emptyText: <EarningsEmptyState isSearch={!!searchFilter?.length} />,
      }}
    />
  );
};

export default EarningsTable;
